import { AuthProvider } from 'react-admin';
import { canAccess } from '../commons/canAccess';
import { getAppStatus } from './index';
import { waitForAuthUser } from '../../data/authUtils';
import { getAuth, signInWithEmailAndPassword, signOut } from 'firebase/auth';
import { getFirestore, doc, getDoc } from 'firebase/firestore';
import { reportIfLegalRequired } from '../../legal/legalRequired';
import type { LegalState } from '../../legal/legalState';

export const authProvider: AuthProvider = {
    login: async ({ username, password }) => {
        // The cached record is the last user's. A login is not always
        // preceded by a logout (the login page can be opened while signed
        // in), and whoever signs in now must not start with that record.
        cachedUser = null;
        lastUserFetch = 0;
        cacheGeneration += 1;
        await signInWithEmailAndPassword(getAuth(), username, password);
    },

    logout: async () => {
        cachedUser = null;
        lastUserFetch = 0;
        cacheGeneration += 1;
        await signOut(getAuth());
    },

    checkAuth: async () => {
        if (
            window.location.pathname === '/set-password' ||
            window.location.hash.includes('#/set-password') ||
            window.location.pathname === '/forgot-password' ||
            window.location.hash.includes('#/forgot-password') ||
            window.location.pathname === '/sign-up' ||
            window.location.hash.includes('#/sign-up') ||
            window.location.pathname === '/change-temp-password' ||
            window.location.hash.includes('#/change-temp-password')
        ) {
            return;
        }

        // Wait for the module-level auth gate to resolve so we never see
        // a premature null during Firebase's IndexedDB hydration.
        const user = await waitForAuthUser();
        if (!user) {
            console.error('checkAuth: No authenticated user detected.');
            throw { redirectTo: '/login' };
        }

        // Fetch app status using the utility function
        const appStatus = await getAppStatus();
        if (appStatus.app_locked) {
            console.warn('Login denied: App is locked.');
            await signOut(getAuth());
            throw {
                redirectTo: '/maintenance',
                message: 'The system is currently disabled.',
            };
        }

        // Fetch user data (using cache)
        const firestoreUser = await getUserFromCache();
        if (!firestoreUser) {
            console.error('User authenticated but missing in Firestore');
            throw { redirectTo: '/sign-up' };
        }

        // If only admins can log in, check if the user is an admin
        if (appStatus.admin_only && firestoreUser.role !== 'admin') {
            console.warn('Login denied: Only admins are allowed.');
            await signOut(getAuth());
            throw {
                redirectTo: '/login',
                message: 'Only administrators can log in at this time.',
            };
        }

        if (firestoreUser.status === 'invited') {
            const expiresAtMs =
                firestoreUser.invite_expires_at && firestoreUser.invite_expires_at.toMillis
                    ? firestoreUser.invite_expires_at.toMillis()
                    : 0;
            if (expiresAtMs && expiresAtMs < Date.now()) {
                await signOut(getAuth());
                throw {
                    redirectTo: '/login',
                    message: 'Invite expired — contact your administrator.',
                };
            }
            // react-admin's requireAuth drops checkAuth redirectTo, so navigate
            // ourselves and never resolve (avoids a flash of the protected layout).
            window.location.replace('/change-temp-password');
            return new Promise<void>(() => {});
        } else if (firestoreUser.status !== 'approved') {
            console.warn('User not approved. Access denied.');
            await signOut(getAuth());
            throw {
                redirectTo: '/login',
                message: 'Your account is disabled. Contact your administrator.',
            };
        }
    },

    checkError: async error => {
        // A legal_required refusal is not a session problem. LegalGate shows
        // the dialog; logging the user out here would only hide it.
        if (reportIfLegalRequired(error)) return;
        if (error?.status === 401 || error?.status === 403) {
            throw new Error('Unauthorized');
        }
    },

    getIdentity: async () => {
        const user = await getUserFromCache();
        if (!user)
            throw new Error('getIdentity failed: no user found in cache');
        return user;
    },

    getPermissions: async () => {
        const user = await getUserFromCache();
        return user?.role ?? null;
    },

    //  Role-based access control
    canAccess: async params => {
        const user = await getUserFromCache();
        if (!user) return false;
        return canAccess(user.role, params);
    },
};

let cachedUser: any = null;
let lastUserFetch = 0;
const USER_CACHE_DURATION = 5 * 60 * 1000; // 5 minutes
// Counts the writes to the cache that do not come from a read (a recorded
// legal decision, a logout). A read that was on its way during one brings an
// older record: it must not replace the cache, let alone mark it fresh.
let cacheGeneration = 0;

const getUserFromCache = async (forceRefresh = false) => {
    const now = Date.now();
    const generation = cacheGeneration;

    if (
        forceRefresh ||
        !cachedUser ||
        now - lastUserFetch > USER_CACHE_DURATION
    ) {
        // Use waitForAuthUser rather than getAuth().currentUser to avoid the
        // token refresh race where currentUser is briefly null.
        const firebaseUser = await waitForAuthUser();
        if (!firebaseUser) return cachedUser ?? undefined;

        try {
            // Fetch directly via Firestore SDK rather than through the
            // react-admin-firebase dataProvider, which has its own broken
            // internal auth state and fails during token refresh.
            const db = getFirestore();
            const snap = await getDoc(doc(db, 'users', firebaseUser.uid));
            if (!snap.exists()) return cachedUser ?? undefined;
            if (generation !== cacheGeneration) return cachedUser ?? undefined;
            cachedUser = { id: snap.id, ...snap.data() };
            lastUserFetch = now;
        } catch (error) {
            console.error('Failed to fetch user from Firestore:', error);
            return cachedUser ?? undefined;
        }
    }

    return cachedUser;
};

/**
 * Put the `legal` state returned by `recordLegalDecisions` into the cached
 * user, so the app continues without a reload. Returns the updated user, or
 * null when none is cached. The caller updates the identity query as well.
 */
export const setCachedUserLegal = (legal: LegalState) => {
    if (!cachedUser) return null;
    cachedUser = { ...cachedUser, legal };
    cacheGeneration += 1;
    return cachedUser;
};

/**
 * Re-read `users/{uid}` now, whatever the age of the cache. Used when the
 * server refuses a request with `legal_required`: the cached copy is then
 * known to be out of date.
 */
export const refreshCachedUser = () => getUserFromCache(true);
